import * as React from 'react';
import { Draft } from '@resi-media/resi-ui';
import { MAX_DURATION_SECONDS } from '@studio/constants/events';
import { getEncoderScheduleTimeRange } from '@studio/helpers/getEncoderScheduleTimeRange';
import { useClient } from '../use-client';

const MINUTES_TO_TRIGGER_OCCUPIED_WARNING = 60;

const useIsEncoderOccupied = ({ encoderId, timeRangeMinutes }: { encoderId: string; timeRangeMinutes?: number }) => {
  const utils = Draft.useUtils<Date>();
  const getSchedules = useClient({
    config: useClient.central.v3.schedules.GET,
    query: {
      // This doesn't really matter right now because the scheduling api adds a bunch of buffer time EVENT-1831
      startTime: new Date(Date.now()).toISOString(),
      endTime: new Date(Date.now() + MAX_DURATION_SECONDS * 1000).toISOString(),
    },
  });
  const { callApi, data, error, isFetching } = getSchedules;

  React.useEffect(() => {
    if (encoderId && !error && !data && !isFetching) {
      callApi();
    }
  }, [callApi, data, encoderId, error, isFetching]);

  const schedulesAssociatedToEncoder = React.useMemo(() => {
    if (data) {
      return data
        .filter((schedule) => schedule.encoderSchedule?.encoderId === encoderId)
        .filter((schedule) => {
          const closestOccurrenceStartTime = Boolean(schedule.occurrences.length) && schedule.occurrences[0].startTime;
          const startRange =
            closestOccurrenceStartTime &&
            getEncoderScheduleTimeRange({
              contentScheduleLocalStartTime: schedule.localStartTime,
              encoderSchedule: schedule.encoderSchedule,
              eventStartDateTime: new Date(closestOccurrenceStartTime),
            });
          if (startRange) {
            const diff = utils.getDiff(startRange.start, new Date(), 'minutes');
            return diff >= 0 && diff <= (timeRangeMinutes ?? MINUTES_TO_TRIGGER_OCCUPIED_WARNING);
          }
          return false;
        });
    }
    return undefined;
  }, [data, timeRangeMinutes, encoderId, utils]);
  return {
    error,
    isFetching,
    data: schedulesAssociatedToEncoder,
  };
};

export default useIsEncoderOccupied;
